'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { CirclePercent, Gift, LoaderCircle, Medal, Minus, Plus, Trash2 } from 'lucide-react'
import { formatPrice } from '@/utils/util'
import { navigateUrl } from '@/utils/client-request'
import { useCartItem } from '@/lib/commerce/hooks'

type Pending = null | 'remove' | 'add' | 'sub'

export default function CartCard({ product }: { product: any }) {
  const pathname = usePathname()
  const c = useCartItem(product)
  const [pending, setPending] = useState<Pending>(null)
  const [prevLoading, setPrevLoading] = useState(c.loading)
  if (prevLoading !== c.loading) {
    setPrevLoading(c.loading)
    if (!c.loading && pending) setPending(null)
  }

  const run = (kind: Exclude<Pending, null>, fn: () => void) => {
    setPending(kind)
    fn()
  }
  const onRemove = () => run('remove', c.handleRemove)
  const onAdd = () => run('add', c.handleAddOne)
  const onSub = () => run('sub', c.handleRemoveOne)
  const busy = (kind: Exclude<Pending, null>) => pending === kind && c.loading

  const labels = (
    <div className="flex flex-wrap items-center gap-1.5">
      {c.productData?.isDigital && (
        <span
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(96,165,250,0.16)', color: '#93c5fd', border: '1px solid rgba(96,165,250,0.3)' }}
        >
          Дижитал
        </span>
      )}
      {c.showDiscountedPrice && c.discountName && (
        <span
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded"
          style={c.isReward
            ? { background: 'rgba(16,185,129,0.16)', color: '#6ee7b7', border: '1px solid rgba(16,185,129,0.3)' }
            : { background: 'rgba(168,85,247,0.16)', color: '#d8b4fe', border: '1px solid rgba(168,85,247,0.3)' }}
        >
          {c.isReward ? <Gift className="w-3 h-3 mr-1 shrink-0" strokeWidth={2.5} /> : <CirclePercent className="w-3 h-3 mr-1 shrink-0" strokeWidth={2.5} />}
          <span className="line-clamp-1">{c.discountName}</span>
        </span>
      )}
      {c.loyaltyDeal && (
        <span
          className="inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.08em] px-1.5 py-0.5 rounded whitespace-nowrap"
          style={{ background: 'rgba(139,92,246,0.16)', color: '#c4b5fd', border: '1px solid rgba(139,92,246,0.3)' }}
        >
          <Medal className="w-3 h-3 mr-1 shrink-0" strokeWidth={2.5} /> Лоялти −{c.loyaltyDeal.percent}%
        </span>
      )}
    </div>
  )

  const priceBlock = (
    <div className="text-[14px] leading-6 font-normal">
      {c.showDiscountedPrice ? (
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-0.5 sm:gap-2">
          <span
            className="font-semibold tabular-nums leading-7 tracking-[.025em]"
            style={{ color: c.unitPrice === 0 ? '#34d399' : '#fda4af' }}
          >
            {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
          </span>
          <span className="text-[12px] tabular-nums line-through" style={{ color: 'rgba(255,255,255,0.4)' }}>
            {formatPrice(c.qty * (c.originalPrice || 0))}
          </span>
        </div>
      ) : (
        <span className="tabular-nums" style={{ color: c.unitPrice === 0 ? '#34d399' : 'white' }}>
          {c.unitPrice === 0 ? 'Үнэгүй' : formatPrice(c.displayPrice)}
        </span>
      )}
    </div>
  )

  const qtyControls = (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onRemove}
        disabled={!!pending}
        className="h-9 w-9 grid place-items-center rounded-md cursor-pointer transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        style={{ background: 'rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {busy('remove') ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" strokeWidth={1.5} />}
      </button>
      {c.isReward ? (
        <div
          className="flex items-center rounded-md px-3 py-1.5"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <span className="text-[13px] min-w-7 text-center text-white tabular-nums">{c.qty}</span>
        </div>
      ) : c.isOutOfStock ? (
        <div
          className="flex items-center rounded-md px-3 py-1.5"
          style={{ background: 'rgba(244,63,94,0.16)', border: '1px solid rgba(244,63,94,0.3)' }}
        >
          <div className="text-[11px] font-medium" style={{ color: '#fda4af' }}>Бараа дууссан</div>
        </div>
      ) : (
        <div
          className="flex items-center rounded-md overflow-hidden"
          style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <button
            type="button"
            onClick={onSub}
            disabled={!!pending}
            className="h-9 w-9 grid place-items-center cursor-pointer text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
          >
            {busy('sub') ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Minus className="h-3.5 w-3.5" />}
          </button>
          <span className="text-[13px] px-2 min-w-7 text-center text-white tabular-nums">{c.qty}</span>
          <button
            type="button"
            onClick={onAdd}
            disabled={!!pending}
            className="h-9 w-9 grid place-items-center cursor-pointer text-white disabled:opacity-40 disabled:cursor-not-allowed transition-opacity hover:opacity-80"
          >
            {busy('add') ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
          </button>
        </div>
      )}
    </div>
  )

  const variantRow = c.showVariantName && (
    <div className="flex items-center gap-2 mt-1 text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
      <span>{c.variant?.name}</span>
      {c.dimension && (
        <>
          <span className="w-px h-3" style={{ background: 'rgba(255,255,255,0.2)' }} />
          <span>{c.dimension}</span>
        </>
      )}
    </div>
  )

  const productLink = (className: string) => (
    <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className={`relative ${className}`}>
      <div
        className="w-24 h-24 rounded-lg overflow-hidden"
        style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}
      >
        <img src={c.imageUrl} alt={c.productName} className="w-full h-full object-cover object-center" />
      </div>
      {c.isReward && (
        <div
          className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full grid place-items-center"
          style={{ background: '#065f46', color: '#a7f3d0', border: '1px solid rgba(16,185,129,0.4)' }}
        >
          <Gift className="w-3 h-3" strokeWidth={2.5} />
        </div>
      )}
    </a>
  )

  return (
    <div
      className="py-4 not-last:border-b"
      style={{ borderColor: 'rgba(255,255,255,0.07)' }}
    >
      <div className="hidden sm:flex items-start gap-4">
        {productLink('shrink-0')}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)} className="min-w-0 max-w-full">
              <h3 className="text-[14px] font-medium text-white leading-6 line-clamp-2 wrap-break-word hover:opacity-80 transition-opacity">
                {c.productName}
              </h3>
            </a>
            {labels}
          </div>
          {variantRow}
          <div className="flex items-center justify-between mt-3 gap-3">
            {priceBlock}
            {qtyControls}
          </div>
        </div>
      </div>

      <div className="sm:hidden flex flex-col gap-3">
        <div className="flex gap-3">
          {productLink('shrink-0')}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex flex-col gap-1 min-w-0">
                <a href={navigateUrl(`/product/${c.productCustomId}`, pathname)}>
                  <h3 className="text-[14px] font-medium text-white leading-6 line-clamp-2 wrap-break-word">{c.productName}</h3>
                </a>
                {labels}
                {c.showVariantName && (
                  <div className="text-[12px]" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    <span>{c.variant?.name}</span>
                    {c.dimension && <span className="ml-2">{c.dimension}</span>}
                  </div>
                )}
              </div>
              {priceBlock}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center gap-3">
          <div className="text-[12px] font-medium uppercase tracking-[0.12em]" style={{ color: 'rgba(255,255,255,0.55)' }}>Тоо хэмжээ</div>
          {qtyControls}
        </div>
      </div>
    </div>
  )
}
